/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1727740800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Oct 01 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "metabase",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Raw SQL",
          "depth": 1,
          "id": "raw-sql"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Hidden Columns",
          "depth": 1,
          "id": "hidden-columns"
        }, {
          "value": "Renaming Columns",
          "depth": 1,
          "id": "renaming-columns"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Metabase limits query results to one million rows. If you need to sync a\nlarger dataset, connect your warehouse directly to Hightouch."
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you pull data stored in ", _jsx(_components.a, {
          href: "https://metabase.com/",
          children: "Metabase"
        }), " and push it to downstream destinations. You can sync data from Metabase Cloud or your own self-hosted instance. Most of the setup occurs in the Hightouch UI, but you need access to Metabase for information like your server URL and credentials."]
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Metabase"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Metabase"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Server"
          }), ": The URL for your Metabase server, for example, ", _jsx(_components.code, {
            children: "https://mycompanyname.metabaseapp.com/"
          }), ". Alternatively, you may provide an IP address and port number, for example ", _jsx(_components.code, {
            children: "127.0.0.1:443"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal Metabase login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Database for native SQL queries"
          }), ": By default, this source supports reading data from Metabase questions and tables. If you wish to execute ", _jsx(_components.a, {
            href: "https://www.metabase.com/docs/latest/questions/native-editor/writing-sql.html",
            children: "native SQL queries"
          }), ", you must specify the name of the database to be queried."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Metabase as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Metabase."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows to extract data from Metabase ", _jsx(_components.a, {
          href: "https://www.metabase.com/glossary/question",
          children: "questions"
        }), " and ", _jsx(_components.a, {
          href: "https://www.metabase.com/glossary/table",
          children: "tables"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the Metabase source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Browse available data"
          }), ". You can then select the ", _jsx(_components.strong, {
            children: "question"
          }), " or ", _jsx(_components.strong, {
            children: "table"
          }), " you want to query. You can query both private and public questions and tables."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your model a descriptive ", _jsx(_components.strong, {
            children: "Name"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "raw-sql",
        children: "Raw SQL"
      }), "\n", _jsxs(_components.p, {
        children: ["You can run queries against your warehouse via Metabase using Hightouch models. If you've connected a warehouse to your Metabase instance, copy the name in the Metabase UI and add it to the ", _jsx(_components.strong, {
          children: "Database for native SQL queries"
        }), " field in the ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "source configuration"
        }), " form."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then, use the Hightouch ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "the SQL editor"
        }), " to create your model."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "hidden-columns",
        children: "Hidden Columns"
      }), "\n", _jsx(_components.p, {
        children: "There is a known issue when querying Metabase questions and tables containing hidden columns. Hightough recommends unhiding all columns."
      }), "\n", _jsx(_components.h3, {
        id: "renaming-columns",
        children: "Renaming Columns"
      }), "\n", _jsx(_components.p, {
        children: "There is a known issue when querying Metabase questions and tables containing renamed columns. Hightough recommends not renaming any columns."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
